import React from 'react';
import { Box } from '@mui/material';
import { styled, css } from '@mui/material/styles';
import ArticleCardsList from '@/components/molecules/Seo/ArticleCardsList';
import Breadcrumbs, {
  BreadcrumbItem,
} from '@/components/atoms/Breadcumbs/Breadcrumbs';
import PreFooter from '@/components/organisms/PreFooter/PreFooter';
import { useTranslation } from '@/src/i18n';
import { APP_ROUTES } from '@/src/constants';

import {
  TlcSearchbarSection,
  TlcShortcutsSection,
  TlcOnlinePractitionersSection,
  TlcAdvantagesSection,
  TlcHowItWorkSection,
  TlcReimbursementSection,
  TlcWhatIsItSection,
  TlcPharamcySection,
} from './Sections';

const Section = styled(Box)(
  ({ theme }) => css`
    padding: ${theme.spacing(3)};

    // Magic numbers according to the background, change carefully
    &.with-wave {
      position: relative;
      margin-top: 70px;

      &::before {
        content: '';
        position: absolute;
        top: -70px;
        left: 0;
        right: 0;
        background: ${theme.palette.background.xLightBlue};
        clip-path: url(#wave-shape);
        -webkit-clip-path: url(#wave-shape);
        height: 125px;
      }

      ${theme.breakpoints.up('sm')} {
        margin-top: 100px;

        &::before {
          top: -100px;
          height: 175px;
        }
      }

      ${theme.breakpoints.up('md')} {
        margin-top: 150px;

        &::before {
          top: -150px;
          height: 300px;
        }
      }
    }
  `,
);

const Container = styled(Box)<{ width?: 'NARROW' | 'WIDE' }>(
  ({ theme, width = 'WIDE' }) => {
    const maxWidth = {
      NARROW: '760px',
      WIDE: '1156px',
    };
    return css`
      position: relative;
      max-width: ${maxWidth[width]};
      margin: 0 auto;

      .blue-links a {
        color: ${theme.palette.text.link};
        text-decoration: underline;
      }
    `;
  },
);

const TlcStaticPage = ({ articles }) => {
  const { t } = useTranslation();

  const breadcrumbs: BreadcrumbItem[] = [
    {
      name: 'Maiia',
      url: APP_ROUTES.HOME,
    },
    {
      name: t('teleconsultation'),
      url: APP_ROUTES.TLC.BASE_URL,
    },
  ];

  return (
    <div className="tlc-static page">
      <Section>
        <Container width="NARROW">
          <TlcSearchbarSection />
        </Container>
      </Section>

      <TlcShortcutsSection />

      <Section className="with-wave" sx={{ bgcolor: 'background.xLightBlue' }}>
        <Container
          width="NARROW"
          sx={{ marginTop: { sm: '-50px', md: '-70px' } }}
        >
          <TlcOnlinePractitionersSection />
        </Container>
      </Section>

      <Section bgcolor="background.primary">
        <Container>
          <TlcAdvantagesSection />
        </Container>
      </Section>

      <Section sx={{ background: 'background.gradientPrimary' }}>
        <Container>
          <TlcHowItWorkSection />
        </Container>
      </Section>

      <Section bgcolor="background.primary">
        <Container>
          <TlcReimbursementSection />
        </Container>
      </Section>

      <Section bgcolor="background.xLightBlue">
        <Container>
          <TlcWhatIsItSection />
        </Container>
      </Section>

      <Section bgcolor="background.primary">
        <Container>
          <TlcPharamcySection />
        </Container>
      </Section>

      <Section bgcolor="background.primary">
        <Container>
          <PreFooter>
            <Breadcrumbs items={breadcrumbs} />
            <ArticleCardsList articles={articles} />
          </PreFooter>
        </Container>
      </Section>

      {/* Clip path wave for masking */}
      <svg className="clip-svg">
        <clipPath id="wave-shape" clipPathUnits="objectBoundingBox">
          <path d="m0.001,0 a0.322,0.28,0,0,0,0.111,0.09 c0.027,0.015,0.077,0.03,0.128,0.035 c0.251,0.027,0.385,0.034,0.539,0.119 c0.116,0.057,0.221,0.255,0.221,0.255 s0.002,0.497,0,0.5 s-0.999,0,-0.999,0 s-0.001,-1,0,-0.999 l0,0" />
        </clipPath>
      </svg>
    </div>
  );
};

export default TlcStaticPage;
